import { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  Schedule,
  People,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { el, enUS } from 'date-fns/locale';
import { ThemedPaper } from '@/components/ThemedPaper';
import { LoadingButton } from '@/components/LoadingButton';
import { useReservation } from '@/hooks/useReservation';
import { useConfirmReservation } from '@/hooks/useConfirmReservation';
import { useCancelReservation } from '@/hooks/useCancelReservation';
import { useDomain, useReservePath } from '@/hooks/useDomain';
import { LanguageSelector } from '@/components/LanguageSelector';
import { formatReservationDate, formatTimeSlot } from '@/utils/date';
import { getErrorMessage } from '@/utils/error';
import { ReservationStatusEnum } from '@/api/generated';
import type { ReservationStoreInfoResponseDto } from '@/api/generated';

interface OutletContext {
  storeInfo?: ReservationStoreInfoResponseDto;
}

/**
 * Reservation management page - view, confirm, or cancel a reservation
 */
export function ReservationManagement() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { reservationUuid } = useParams<{ reservationUuid: string }>();
  const domain = useDomain();
  const reservePath = useReservePath();
  const { storeInfo } = useOutletContext<OutletContext>();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<'confirmed' | 'cancelled' | null>(null);

  const {
    data: reservation,
    isLoading,
    error: fetchError,
  } = useReservation(domain, reservationUuid ?? null);

  const {
    mutate: confirmReservation,
    isPending: isConfirming,
    error: confirmError,
  } = useConfirmReservation(domain);

  const {
    mutate: cancelReservation,
    isPending: isCancelling,
    error: cancelError,
  } = useCancelReservation(domain);

  const handleBack = () => {
    navigate(reservePath);
  };

  const handleConfirm = () => {
    if (!reservationUuid) return;
    confirmReservation(reservationUuid, {
      onSuccess: () => {
        setActionSuccess('confirmed');
      },
    });
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = () => {
    if (!reservationUuid) return;
    cancelReservation(reservationUuid, {
      onSuccess: () => {
        setActionSuccess('cancelled');
        setShowCancelDialog(false);
      },
    });
  };

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false);
  };

  if (isLoading) {
    return (
      <ThemedPaper>
        <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </ThemedPaper>
    );
  }

  if (fetchError) {
    return (
      <ThemedPaper>
        <Box p={4}>
          <Box mb={3}>
            <Button startIcon={<ArrowBack />} onClick={handleBack}>
              {t('common.back')}
            </Button>
          </Box>
          <Alert severity="error">{t('reservationManagement.fetchError')}</Alert>
          <Box mt={3}>
            <Button variant="contained" onClick={handleBack}>
              {t('reservationManagement.makeNew')}
            </Button>
          </Box>
        </Box>
      </ThemedPaper>
    );
  }

  if (!reservation) {
    return null;
  }

  const isPending =
    reservation.status === ReservationStatusEnum.Pending ||
    reservation.status === ReservationStatusEnum.Requested;

  const isCancelled = reservation.status === ReservationStatusEnum.Cancelled;

  const isArchived =
    reservation.status === ReservationStatusEnum.NotShown ||
    reservation.status === ReservationStatusEnum.HasLeft ||
    reservation.status === ReservationStatusEnum.Cancelled ||
    reservation.status === ReservationStatusEnum.Declined;

  const dateLocales = { en: enUS, el: el };
  const currentLang = i18n.language.split('-')[0] as keyof typeof dateLocales;
  const dateLocale = dateLocales[currentLang] || enUS;

  const formattedDate = formatReservationDate(
    reservation.reservation_starts,
    'EEE, d MMM yyyy',
    dateLocale
  );
  const formattedTime = formatTimeSlot(
    reservation.reservation_starts,
    reservation.reservation_ends,
    storeInfo?.show_duration ?? false
  );

  const customerName = reservation.customer
    ? `${reservation.customer.first_name} ${reservation.customer.last_name}`
    : t('reservationManagement.unknownCustomer');

  const getStatusColor = () => {
    if (isArchived) {
      return { bgcolor: 'error.main', color: 'error.contrastText' };
    }
    return { bgcolor: 'primary.main', color: 'primary.contrastText' };
  };

  const statusColors = getStatusColor();
  const error = confirmError || cancelError;

  const partySize = reservation.adults + reservation.children;

  return (
    <ThemedPaper>
      <Box p={4}>
        {/* Header with title and language selector */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h2">
            {t('reservationManagement.title')}
          </Typography>
          <LanguageSelector />
        </Box>

        {/* Status pill - full width, centered */}
        <Box
          sx={{
            ...statusColors,
            borderRadius: '50px',
            py: 1.5,
            px: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          <Typography variant="body1">
            {t('reservationManagement.statusLabel')}:
          </Typography>
          {isCancelled ? <Cancel fontSize="small" /> : <CheckCircle fontSize="small" />}
          <Typography variant="body1" fontWeight="medium">
            {t(`reservationManagement.status.${reservation.status}`, reservation.status)}
          </Typography>
        </Box>

        {/* Details row - horizontal, centered */}
        <Box display="flex" justifyContent="center" alignItems="center" gap={3} mb={3}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <People fontSize="small" color="action" />
            <Typography variant="body2">
              {partySize} {t('reservationManagement.people')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="body2">{formattedTime}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2">{formattedDate}</Typography>
          </Box>
        </Box>

        {/* Name - centered */}
        <Typography variant="body1" align="center" mb={3}>
          {t('reservationManagement.name')}: <strong>{customerName}</strong>
        </Typography>

        {/* Success/Error alerts */}
        {actionSuccess === 'confirmed' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('reservationManagement.confirmationSuccess')}
          </Alert>
        )}

        {actionSuccess === 'cancelled' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('reservationManagement.cancellationSuccess')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {getErrorMessage(error, storeInfo?.widget_settings)}
          </Alert>
        )}

        {/* Action buttons - side by side */}
        {!isArchived && !actionSuccess && (
          <Box display="flex" gap={2}>
            <LoadingButton
              variant="outlined"
              color="error"
              size="large"
              sx={{ flex: 1 }}
              loading={isCancelling}
              onClick={handleCancelClick}
              endIcon={!isCancelling ? <Cancel /> : undefined}
            >
              {isCancelling
                ? t('reservationManagement.cancelling')
                : t('reservationManagement.cancelReservation')}
            </LoadingButton>
            {isPending && (
              <LoadingButton
                variant="contained"
                color="primary"
                size="large"
                sx={{ flex: 1 }}
                loading={isConfirming}
                onClick={handleConfirm}
                endIcon={!isConfirming ? <CheckCircle /> : undefined}
              >
                {isConfirming
                  ? t('reservationManagement.confirming')
                  : t('reservationManagement.confirmReservation')}
              </LoadingButton>
            )}
          </Box>
        )}

        {/* Cancel confirmation dialog */}
        <Dialog open={showCancelDialog} onClose={handleCancelDialogClose}>
          <DialogTitle>{t('reservationManagement.cancelDialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('reservationManagement.cancelDialogMessage')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialogClose}>{t('reservationManagement.cancelDialogNo')}</Button>
            <Button onClick={handleCancelConfirm} color="error" autoFocus>
              {t('reservationManagement.cancelDialogYes')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemedPaper>
  );
}
